import React from 'react';
import AppConfig from 'appConfig';
import AppStore from 'fetch/AppFetch';
import { USER_TYPE_ZYWAVE } from 'lib/config/appConstants';
import { isZywaveUrl } from 'lib/AppHelpers';

/**
 * SessionExpiredMessage Component
 *
 * A reusable TypeScript component that displays the session expiration message.
 * This follows the DRY principle by centralizing the session message UI logic.
 * The message content is controlled by AppConfig.sessionExpiredMessages.
 *
 * This component respects the enableRedirectZywaveDotCom feature flag for consistent UX.
 * It displays different messages based on whether the user is a Zywave user (account_type === 7)
 * or if the current URL is a zywave.com domain.
 *
 * Used in: Layout, LayoutTabs, AlternateAddressesTable, TrackExports
 *
 * @returns {JSX.Element} The session expired message UI
 */
const SessionExpiredMessage: React.FC = (): JSX.Element => {
  // Get user info to determine if this is a Zywave user
  const userInfo = AppStore.fetchUserInfo();

  // Check if user is a Zywave user (account_type === 7) or if on zywave.com URL
  const isZywaveUser = userInfo?.account_type === USER_TYPE_ZYWAVE;
  const isOnZywaveUrl = isZywaveUrl();

  // Use Zywave-specific messaging if user is a Zywave user OR if on zywave.com URL
  const shouldUseZywaveMessages = isZywaveUser || isOnZywaveUrl;

  return shouldUseZywaveMessages ? (
    // Display Zywave-specific messages
    <div>
      <div>{AppConfig.sessionExpiredMessages.zywave.primary}</div>
    </div>
  ) : (
    // Display standard messages for legacy users
    <div>
      <div>{AppConfig.sessionExpiredMessages.primary}</div>
      <div>{AppConfig.sessionExpiredMessages.secondary}</div>
    </div>
  );
};

export default SessionExpiredMessage;
